import React, { useEffect } from 'react'
import PropTypes, { node } from 'prop-types'
import * as Styled from './SearchBar.styles'

// component stack
import { Icon, Input, Divider, Button } from 'components/atoms'
import Autocomplete from './partials/Autocomplete/Autocomplete'
import Modal from '../Modal/Modal'

// hoc stack
import { useScrollLock, useMobileSwitch, useRedirects } from 'hooks'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import { useSelector } from 'react-redux'
import { getRadiusOptions } from 'settings/app/helpers'
import { getSearchUrl } from 'settings/app/urlHelpers'
import { PAGE_TYPES, entryPointId } from 'settings/app/constants'

/**
 * search bar
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const SearchBar = (props) => {
  const {
    // params
    query,
    location,
    radius,
    search,
    locationCoordinates,
    searchButtonDisabled,
    // autocomplete
    renderQueryOptions,
    renderLocationOptions,
    autoClusterError,
    // input states
    searchLoading,
    locationLoading,
    // handlers
    onChangeQuery,
    onChangeLocation,
    onChangeRadius,
    onDeleteButtonClicked,
    onSearchButtonClicked,
    onLocateButtonClicked,
    onQueryEnter,
    onLocationEnter,
    checkLocationErrorState,
    pageType,
    // abs2 selects
    additionalSelects,
    additionalSelectsValues,
  } = props

  // helpers && global state
  const isMobile = useMobileSwitch()
  const textContext = React.useContext(TextContext)
  const { application } = useSelector((state) => state)
  const { entryPoint } = application
  const redirectURLs = useRedirects()

  // flags for auto complete based errors
  const queryError = autoClusterError.type === 'query' && autoClusterError.error
  const locationError =
    autoClusterError.type === 'location' && autoClusterError.error

  // local states
  const [showLocationAdornment, setShowLocationAdornment] =
    React.useState(false)
  const [showQueryOverlay, setShowSearchOverlay] = React.useState(false)
  const [showLocationOverlay, setShowLocationOverlay] = React.useState(false)
  const [redirectOnSearch, setRedirectOnSearch] = React.useState(null)
  useScrollLock(isMobile && (showQueryOverlay || showLocationOverlay))

  // redirect flag
  useEffect(() => {
    if (pageType === PAGE_TYPES.START_PAGE) {
      setRedirectOnSearch(true)
    } else {
      setRedirectOnSearch(false)
    }
  }, [entryPoint, pageType])

  /**
   * deep link for redirection in production mode
   * @returns {string}
   */
  const getDeepLinkForRedirect = () =>
    `${ redirectURLs[entryPoint] }${ getSearchUrl({
      query,
      location,
      key: search.key,
      type: search.type,
      radius,
      lat: locationCoordinates.lat,
      lng: locationCoordinates.lng,
      initial_search: true,
      ...additionalSelectsValues,
    }) }`

  /**
   * return search button with conditional handler
   * @returns {*}
   */
  const getSearchButton = () => (
    <Styled.SearchButton
      id={ 'search-btn' }
      data-cy={ 'search-btn' }
      text={ textContext['searchBar.search.button'] }
      onClick={ () => !redirectOnSearch && onSearchButtonClicked() }
      disabled={ searchButtonDisabled }
    />
  )

  const getSearchButtonByPageTyp = () => (
    <div className='column button-col'>
      {redirectOnSearch ? (
        <a
          data-cy='search-btn-wrapper'
          href={ getDeepLinkForRedirect() }
          onClick={ (e) => validateSearchParamsOnRedirect(e) }>
          {getSearchButton()}
        </a>
      ) : (
        getSearchButton()
      )}
    </div>
  )

  const getQueryLabel = () => {
    switch (entryPoint) {
      case entryPointId.CAREHOMES:
      case entryPointId.CARESERVICES:
      case entryPointId.CARESUPPORT:
        return textContext['searchBar.query.labelOptional']
      default:
        return textContext['searchBar.query.label']
    }
  }

  const handleLocationFocus = () => {
    setShowLocationOverlay(true)
    setShowLocationAdornment(true)
  }

  const handleLocationBlur = () => {
    setShowLocationAdornment(false)
  }

  /**
   * validate before redirect (only on pageType === startPage)
   * @param e
   */
  const validateSearchParamsOnRedirect = async (e) => {
    e.preventDefault()
    let queryIsValid = false
    let locationIsValid = false

    if (query !== '') {
      await onQueryEnter().then(() => {
        queryIsValid = true
      })
    } else {
      queryIsValid = true
    }

    if (location !== '') {
      await checkLocationErrorState().then(() => (locationIsValid = true))
    } else {
      locationIsValid = true
    }

    if (queryIsValid && locationIsValid) {
      window.location = getDeepLinkForRedirect()
    }
  }

  const searchBarPaddingClass = !!additionalSelects ? 'multi-row' : 'one-row'

  return (
    <Styled.SearchBar>
      <div className={ `searchbar-row ${ searchBarPaddingClass }` }>
        <div className='column query-col'>
          <Autocomplete
            id='autocomplete-query'
            data-cy='autocomplete-query'
            hasError={ queryError }
            renderInput={
              <Input
                id='search'
                dataCy='search-field'
                data-cy='search-field'
                placeholder={ textContext[`searchBar.query.${ entryPoint }`] }
                label={ getQueryLabel() }
                loading={ searchLoading }
                onChange={ (value) => onChangeQuery(value) }
                onFocus={ () => setShowSearchOverlay(true) }
                onEnter={ onQueryEnter }
                value={ query }
                fullWidth
                onDeleteButtonClicked={ () => onDeleteButtonClicked('query') }
                deleteButtonId='delete-query'
                deleteButtonAriaLabel='Suchbegriff löschen'
                hasError={ queryError }
                errorMessage={ queryError && autoClusterError.errorMessage }
                hasAutoComplete={ false !== renderQueryOptions }
              />
            }
            renderOptions={ !isMobile && renderQueryOptions }
            maxOptions={ 5 }
          />
        </div>
        <div className='column location-col'>
          <Styled.LocationContainer>
            <Autocomplete
              id='autocomplete-location'
              data-cy='autocomplete-location'
              hasError={ locationError }
              renderInput={
                <Input
                  id='location'
                  dataCy='location-field'
                  data-cy='location-field'
                  placeholder={ textContext['searchBar.location.placeholder'] }
                  label={ textContext['searchBar.location.label'] }
                  loading={ locationLoading }
                  value={ location }
                  onChange={ (value) => onChangeLocation(value) }
                  onFocus={ handleLocationFocus }
                  onBlur={ handleLocationBlur }
                  onEnter={ onLocationEnter }
                  startAdornment={ <Icon className='icon-f-map-marker' /> }
                  type='location'
                  onDeleteButtonClicked={ () =>
                    onDeleteButtonClicked('location')
                  }
                  deleteButtonId='delete-location'
                  deleteButtonAriaLabel='Lokalisierung löschen'
                  endAdornment={
                    <>
                      {showLocationAdornment && !isMobile && (
                        <>
                          <Divider orientation='vertical' />
                          <Button
                            id='btn-locate'
                            data-cy='btn-locate'
                            className='icon-f-target'
                            aria-label='Lokalisierung'
                            onClick={ onLocateButtonClicked }
                          />
                        </>
                      )}
                      {location && (
                        <>
                          <Divider orientation='vertical' />
                          <Styled.RadiusSelect
                            id='radius'
                            data-cy='radius'
                            value={ radius }
                            options={ getRadiusOptions(entryPoint) }
                            handleSelect={ (value) => onChangeRadius(value) }
                            backgroundColor='rgba(0,0,0,0)'
                          />
                        </>
                      )}
                    </>
                  }
                  fullWidth
                  hasError={ locationError }
                  errorMessage={ locationError && autoClusterError.errorMessage }
                  hasAutoComplete={ false !== renderLocationOptions }
                />
              }
              renderOptions={ !isMobile && renderLocationOptions }
            />
          </Styled.LocationContainer>
        </div>

        {(!additionalSelects || !isMobile) && getSearchButtonByPageTyp()}
      </div>

      {additionalSelects && (
        <div
          data-cy='additional-selects-wrapper'
          className={ `searchbar-row ${ searchBarPaddingClass }` }>
          {additionalSelects}
        </div>
      )}

      {additionalSelects && isMobile && getSearchButtonByPageTyp()}

      {isMobile && showQueryOverlay && (
        <Modal
          isFullScreen={ true }
          onClickCloseModal={ () => setShowSearchOverlay(false) }>
          <Styled.ModalRow>
            <Autocomplete
              id='autocomplete-query'
              data-cy='autocomplete-query'
              hasError={ queryError }
              renderInput={
                <Input
                  id='modal-search'
                  data-cy='modal-search-field'
                  dataCy='modal-search-field'
                  placeholder={ textContext[`searchBar.query.${ entryPoint }`] }
                  label={ getQueryLabel() }
                  loading={ searchLoading }
                  onChange={ (value) => onChangeQuery(value) }
                  onEnter={ onQueryEnter }
                  startAdornment={ <Icon className='icon-f-magnifier' /> }
                  value={ query }
                  hasFocus={ true }
                  fullWidth
                  onDeleteButtonClicked={ () => onDeleteButtonClicked('query') }
                  deleteButtonId='delete-query'
                  deleteButtonAriaLabel='Suchbegriff löschen'
                  hasError={ queryError }
                  errorMessage={ queryError && autoClusterError.errorMessage }
                />
              }
              renderOptions={ renderQueryOptions }
            />
            <Button
              id='searchModalNext'
              text={ textContext['mobile.next.btn'] }
              onClick={ () => {
                onQueryEnter().then(() => {
                  setShowSearchOverlay(false)
                  setShowLocationOverlay(true)
                })
              } }
            />
          </Styled.ModalRow>
        </Modal>
      )}

      {isMobile && showLocationOverlay && (
        <Modal
          isFullScreen={ true }
          onClickCloseModal={ () => setShowLocationOverlay(false) }>
          <Styled.ModalRow>
            <Autocomplete
              id='autocomplete-location'
              data-cy='autocomplete-location'
              hasError={ locationError }
              renderInput={
                <Input
                  id='modal-location'
                  dataCy='modal-location-field'
                  placeholder={ textContext['searchBar.location.placeholder'] }
                  label={ textContext['searchBar.location.label'] }
                  loading={ locationLoading }
                  value={ location }
                  hasFocus={ true }
                  onChange={ (value) => onChangeLocation(value) }
                  startAdornment={ <Icon className='icon-f-map-marker' /> }
                  onEnter={ onLocationEnter }
                  type='location'
                  onDeleteButtonClicked={ () =>
                    onDeleteButtonClicked('location')
                  }
                  deleteButtonId='delete-location'
                  deleteButtonAriaLabel='Lokalisierung löschen'
                  endAdornment={
                    <>
                      <Divider orientation='vertical' />
                      <Button
                        id='btn-locate'
                        data-cy='btn-locate'
                        className='icon-f-target'
                        onClick={ onLocateButtonClicked }
                      />
                    </>
                  }
                  fullWidth
                  hasError={ locationError }
                  errorMessage={ locationError && autoClusterError.errorMessage }
                />
              }
              renderOptions={ renderLocationOptions }
            />
            <Button
              id='locationModalNext'
              text={ textContext['searchBar.modal.button'] }
              onClick={ () =>
                checkLocationErrorState().then(() =>
                  setShowLocationOverlay(false)
                )
              }
            />
          </Styled.ModalRow>
        </Modal>
      )}
    </Styled.SearchBar>
  )
}

SearchBar.defaultProps = {
  searchButtonDisabled: true,
}

SearchBar.propTypes = {
  /** Query */
  query: PropTypes.string,
  /** Location **/
  location: PropTypes.string,
  /** selected radius for search **/
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  search: PropTypes.shape({
    /** search key something like k40, 1-275 **/
    key: PropTypes.string,
    /** search typ something like ops, icd **/
    type: PropTypes.string,
  }).isRequired,
  /** location coordinates */
  locationCoordinates: PropTypes.shape({
    /** latitude **/
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** longitude **/
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  }),
  /** render query options */
  renderQueryOptions: PropTypes.node,
  /** render location options */
  renderLocationOptions: PropTypes.node,
  /** If true, the search button is disabled */
  searchButtonDisabled: PropTypes.bool,
  /** loading state for indicating xhr requests **/
  searchLoading: PropTypes.bool.isRequired,
  /** loading state for indicating xhr requests **/
  locationLoading: PropTypes.bool.isRequired,
  /** onChange event for query
   * Signature: function(query: string) {...}
   */
  onChangeQuery: PropTypes.func.isRequired,
  /** onChange event for location
   * Signature: function(location: string) {...}
   */
  onChangeLocation: PropTypes.func.isRequired,
  /** search button clicked
   * Signature: function(query: string, location: string, radius: string) {...}
   */
  onChangeRadius: PropTypes.func.isRequired,
  /** handler for radius change
   * Signature: function(query: string, location: string, radius: string) {...}
   */
  onSearchButtonClicked: PropTypes.func.isRequired,
  /** fired when the locate button is clicked
   * Signature: function() {...}
   */
  onLocateButtonClicked: PropTypes.func.isRequired,
  /* click handler for delete input content */
  onDeleteButtonClicked: PropTypes.func.isRequired,
  /** contains autocomplete error **/
  /** Callback fired when enter was pressed in query input */
  onQueryEnter: PropTypes.func,
  /** Callback fired when enter was pressed in location input */
  onLocationEnter: PropTypes.func,
  /** injected from typo plg. (used for controlling redirecting) **/
  pageType: PropTypes.string,
  /** contains error state for the input elements **/
  autoClusterError: PropTypes.shape({
    type: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['location', 'query']),
    ]),
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
  }),
  /** check location error state **/
  checkLocationErrorState: PropTypes.func.isRequired,
  // contains additional select elements
  additionalSelects: node,
  additionalSelectsValues: PropTypes.object,
}

export default SearchBar
